import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { company } from '../config/company';
import InstallFooterLink from './InstallFooterLink';

const Footer = () => {
  const { t, language } = useLanguage();
  const isNL = language === 'nl';

  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        <div style={styles.grid}>
          {/* Brand */}
          <div>
            <h3 style={styles.brandName}>CasaOthers</h3>
            <p style={styles.tagline}>{t('footer.tagline')}</p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 style={styles.footerTitle}>{t('footer.navigation')}</h4>
            <div style={styles.linkList}>
              <Link to="/woningen" style={styles.footerLink} data-testid="footer-properties-link">
                {t('nav.properties')}
              </Link>
              <Link to="/investeren" style={styles.footerLink} data-testid="footer-invest-link">
                {t('nav.invest')}
              </Link>
              <Link to="/blog" style={styles.footerLink} data-testid="footer-blog-link">
                {t('nav.blog')}
              </Link>
              <Link to="/contact" style={styles.footerLink} data-testid="footer-contact-link">
                {t('nav.contact')}
              </Link>
            </div>
          </div>

          {/* Regions */}
          <div>
            <h4 style={styles.footerTitle}>{t('footer.regions')}</h4>
            <div style={styles.linkList}>
              <Link to="/costa-blanca" style={styles.footerLink} data-testid="footer-costa-blanca-link">
                Costa Blanca
              </Link>
              <Link to="/costa-calida" style={styles.footerLink} data-testid="footer-costa-calida-link">
                Costa Calida
              </Link>
              <Link to="/costa-almeria" style={styles.footerLink} data-testid="footer-costa-almeria-link">
                Costa Almeria
              </Link>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h4 style={styles.footerTitle}>{isNL ? 'Juridisch' : 'Legal'}</h4>
            <div style={styles.linkList}>
              <Link to="/privacy" style={styles.footerLink} data-testid="footer-privacy-link">
                {isNL ? 'Privacybeleid' : 'Privacy Policy'}
              </Link>
              <Link to="/cookies" style={styles.footerLink} data-testid="footer-cookies-link">
                {isNL ? 'Cookiebeleid' : 'Cookies Policy'}
              </Link>
              <Link to="/voorwaarden" style={styles.footerLink} data-testid="footer-terms-link">
                {isNL ? 'Algemene Voorwaarden' : 'Terms & Conditions'}
              </Link>
              <Link to="/disclaimer" style={styles.footerLink} data-testid="footer-disclaimer-link">
                Disclaimer
              </Link>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 style={styles.footerTitle}>{t('footer.contact')}</h4>
            <p style={styles.contactText}>{t('footer.questionsWhatsApp')}</p>
            <a
              href={company.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.whatsappButton}
              data-testid="footer-whatsapp-button"
            >
              <MessageCircle size={18} />
              <span>{t('footer.whatsapp')}</span>
            </a>
          </div>
        </div>

        <div style={styles.bottom}>
          <p style={styles.copyright}>
            © {new Date().getFullYear()} {company.brand}. {t('footer.allRights')}
          </p>
          <p style={styles.legalInfo}>
            {company.name} · KvK {company.kvk} · {company.street}, {company.postalCity}
          </p>
          {/* Renders only on browsers that expose beforeinstallprompt AND
              only when the app is not already installed. Never overlaps
              existing legal / copyright text. */}
          <InstallFooterLink />
        </div>
      </div>
    </footer>
  );
};

const styles = {
  footer: {
    backgroundColor: '#E6DCCA',
    borderTop: '1px solid #EAE5DD',
    marginTop: '4rem',
  },
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '4rem 1.5rem 2rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '3rem',
    marginBottom: '3rem',
  },
  brandName: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.75rem',
    color: '#1A1918',
    marginBottom: '0.75rem',
  },
  tagline: {
    color: '#585755',
    fontSize: '0.9rem',
  },
  footerTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.1rem',
    color: '#1A1918',
    marginBottom: '1rem',
    fontWeight: 500,
  },
  linkList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  footerLink: {
    color: '#585755',
    textDecoration: 'none',
    fontSize: '0.9rem',
    transition: 'color 0.3s ease',
  },
  contactText: {
    color: '#585755',
    fontSize: '0.9rem',
    marginBottom: '1rem',
  },
  whatsappButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: '#25D366',
    color: 'white',
    padding: '0.75rem 1.5rem',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.3s ease',
  },
  bottom: {
    borderTop: '1px solid #D4C8B3',
    paddingTop: '2rem',
    textAlign: 'center',
  },
  copyright: {
    color: '#8C8B88',
    fontSize: '0.875rem',
  },
  legalInfo: {
    color: '#8C8B88',
    fontSize: '0.75rem',
    marginTop: '0.5rem',
  },
};

export default Footer;
